import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialog as MatDialog,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { SupplierModel } from 'src/app/core/models/supplier.model';
import { SupplierService } from '../../supplier.service';
import { ELECTRONIC_INVOICE_FIELD_LABEL } from '../../supplier.constants';
import { FormModalBaseComponent } from 'src/app/core/base/components/form-modal-base/form-modal-base.component';
import { SupplierType } from '../../../../../../core/enums/supplier-type.enum';
import { Country } from '../../../../../../core/enums/countries.enum';
import { FrenchTerritoryHelper } from '../../../../../../core/helpers/frenchTerritory.helper';
import { FormSwitchValidatorHelper } from '../../../../../../core/helpers/formSwitchValidator.helper';
import { FormResetManyHelper } from '../../../../../../core/helpers/formResetMany.helper';
import { Observable, of } from 'rxjs';
import { CategoryService } from '../../../../settings/category/category.service';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { map, startWith } from 'rxjs/operators';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { AuthService } from '../../../../../../core/services/auth.service';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { ResponseTypeEnum } from '../../../../../../core/base/enum/response-type.enum';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { DialogConfirmComponent } from '../../../../../../../@vex/components/dialog-confirm/dialog-confirm.component';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { SupplierGouvResultModel } from '../../../../../../core/models/supplier-gouv-result.model';

export function tvaNumberValidator(sirenControl: AbstractControl, countryControl: AbstractControl): AsyncValidatorFn {
  return (tvaControl: AbstractControl): Observable<ValidationErrors | null> => {
    const siren = sirenControl.value;
    const tvaNumber = tvaControl.value;
    const country = countryControl.value;

    if (country !== Country.FR) {
      return of(null);
    }

    // Validation réussie si le champ est vide
    if (!tvaNumber || tvaNumber === '') {
      return of(null);
    }

    // Validation échoue si le numéro de TVA ne contient pas le SIREN correspondant
    if (tvaNumber.substring(4, 13) !== siren) {
      return of({ customError: true });
    }

    // Validation réussie
    return of(null);
  };
}

@Component({
  selector: 'vex-supplier-create-update',
  templateUrl: './supplier-create-update-delete.component.html',
  styleUrls: ['./supplier-create-update-delete.component.scss'],
})
export class SupplierCreateUpdateDeleteComponent extends FormModalBaseComponent<SupplierModel> implements OnInit {
  readonly ELECTRONIC_INVOICE_FIELD_LABEL = ELECTRONIC_INVOICE_FIELD_LABEL;
  supplierType: Array<string>;
  addressForm: UntypedFormGroup;
  countries: Array<any>;
  isFrench = true;
  isIntern = false;
  isExterne = false;
  isParticulier = false;
  isEtranger = false;
  submittedToTva = false;
  origin: any;
  families: Array<any> = [];

  canCreateMode = false;
  canUpdateMode = false;
  canDeleteMode = false;
  canReadMode = false;
  canImportMode = false;
  canArchiveMode = false;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  itemCtrl = new UntypedFormControl();
  filteredFamilies: Observable<string[]>;
  familiesSupplier: Array<any> = [];

  @ViewChild('itemInput') itemInput: ElementRef<HTMLInputElement>;
  @ViewChild('gouvResultsList') gouvResultsList: ElementRef<HTMLDivElement>;

  isDuplicate = false;
  isLinkedInternSupplier = false;

  // Recherche entreprise (API Recherche d'Entreprises — data.gouv.fr, PRED-3215).
  // État 1 : modale compacte, zone de recherche au-dessus de la fiche (saisie libre possible).
  // État 2 : après recherche, la modale s'élargit et les résultats s'affichent à gauche de la fiche.
  gouvSearchForm: UntypedFormGroup;
  gouvResults: SupplierGouvResultModel[] = [];
  gouvTotalPage = 0;
  gouvPage = 1;
  gouvLoading = false;
  hasSearchedGouv = false;
  selectedGouvSiret: string = null;

  private static readonly GOUV_DIALOG_WIDE_WIDTH = '1180px';

  ENUM_PERMISSIONS = ENUM_PERMISSIONS;

  get showSupplierDetails(): boolean {
    return this.isExterne || this.isParticulier || this.isEtranger;
  }

  // Le compte auxiliaire est aussi affiché pour un fournisseur interne lié : un isPredik
  // peut l'affecter comme pour n'importe quel fournisseur (PRED-3210).
  get showSubsidiarAccount(): boolean {
    return this.showSupplierDetails || this.isLinkedInternSupplier;
  }

  // La recherche API gouv ne concerne que la création d'un fournisseur EXTERNE français
  // (le référentiel Recherche d'Entreprises ne couvre que les entreprises immatriculées en France).
  get showGouvSearch(): boolean {
    return this.canCreateMode && this.isExterne && this.isFrench;
  }

  get gouvHasNoCriteria(): boolean {
    const { q, codePostal, ville, departement, activitePrincipale } = this.gouvSearchForm?.value ?? {};
    return !q && !codePostal && !ville && !departement && !activitePrincipale;
  }

  // Sur le nombre de pages, pas sur un compte de lignes : le back filtre les résultats
  // dont le siège ne répond pas aux critères, le total de l'API est donc surestimé.
  get canLoadMoreGouv(): boolean {
    return this.gouvPage < this.gouvTotalPage;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<FormModalBaseComponent<SupplierModel>>,
    public service: SupplierService,
    private categoryService: CategoryService,
    public authService: AuthService,
    private dialog: MatDialog,
    private supplierService: SupplierService,
    public snackbarService: SnackbarService,
  ) {
    super(data, dialogRef, service);
    // Messages de la snackbar en français (sinon le message anglais du back est affiché tel quel).
    this.successCreateMessage = 'Le fournisseur a été créé avec succès';
    this.successUpdateMessage = 'Le fournisseur a été modifié avec succès';
  }

  async ngOnInit() {
    super.ngOnInit();

    this.canCreateMode = this.isCreateMode();
    this.canUpdateMode = this.isUpdateMode();
    this.canDeleteMode = this.isDeleteMode();
    this.canReadMode = this.isReadMode();
    this.canImportMode = this.isImportMode();
    this.canArchiveMode = this.isArchiveMode();

    this.isIntern = this.defaults?.type === SupplierType.INTERNE;
    this.isExterne = this.defaults?.type === SupplierType.EXTERNE;
    this.isParticulier = this.defaults?.type === SupplierType.PARTICULIER;
    this.isEtranger = this.defaults?.type === SupplierType.ETRANGER;
    this.origin = this.defaults;
    this.isFrench = this.isUpdateMode() ? this.defaults?.headquarterAddress?.country === Country.FR : true;
    this.supplierType = Object.values(SupplierType);
    this.countries = Object.values(Country);

    this.isLinkedInternSupplier = this.defaults?.type === SupplierType.INTERNE && !!this.defaults?.supportEstablishment;

    // Fournisseur interne lié à un établissement support : lecture seule, sauf pour un isPredik
    // qui peut affecter le compte auxiliaire et les familles d'achats (PRED-3210).
    if (this.isLinkedInternSupplier && !this.authService.isPredik()) {
      this.mode = CrudModeEnum.Read;
    }

    const countryControl = new UntypedFormControl(
      this.isUpdateMode() ? this.defaults?.headquarterAddress?.country : Country.FR,
    );
    const sirenControl = new UntypedFormControl(this.defaults?.sirenNumber, [
      Validators.required,
      Validators.pattern(/^[0-9]{9}$/),
    ]);
    const tvaControl = new UntypedFormControl(
      this.defaults?.tva,
      [
        Validators.pattern(
          /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|GB|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK)[0-9A-Z]{9,11}$/,
        ),
      ],
      [tvaNumberValidator(sirenControl, countryControl)],
    );

    this.addressForm = new UntypedFormGroup({
      street: new UntypedFormControl(this.defaults?.headquarterAddress?.street, [Validators.required]),
      additionalAddress: new UntypedFormControl(this.defaults?.headquarterAddress?.additionalAddress),
      // ETRANGER : aucun contrôle sur le code postal (formats étrangers non normés).
      postalCode: new UntypedFormControl(
        this.defaults?.headquarterAddress?.postalCode,
        this.isEtranger ? null : [Validators.required, Validators.pattern(/^[0-9]{5}$/)],
      ),
      city: new UntypedFormControl(this.defaults?.headquarterAddress?.city, [Validators.required]),
      country: countryControl,
    });

    this.form = new UntypedFormGroup({
      _id: new UntypedFormControl(this.defaults?._id),
      type: new UntypedFormControl(this.defaults?.type, [Validators.required]),
      label: new UntypedFormControl(this.defaults?.label, [Validators.required]),
      commercialName: new UntypedFormControl(this.defaults?.commercialName),
      siretNumber: new UntypedFormControl(this.defaults?.siretNumber, [Validators.required]),
      sirenNumber: sirenControl,
      codeApe: new UntypedFormControl(this.defaults?.codeApe, [
        Validators.required,
        Validators.pattern(/^\d{4}[A-Z]$/),
      ]),
      dunsNumber: new UntypedFormControl(this.defaults?.dunsNumber, null),
      tva: tvaControl,
      headquarterAddress: this.addressForm,
      subsidiarAccount: new UntypedFormControl(
        this.defaults?.subsidiarAccount,
        this.showSupplierDetails ? [Validators.required, Validators.maxLength(13)] : [Validators.maxLength(13)],
      ),
      submittedToTva: new UntypedFormControl(false),
      // PA-Facture électronique (PRED-3268) : décochée par défaut en création.
      electronicInvoicePlatform: new UntypedFormControl(this.defaults?.electronicInvoicePlatform ?? false),
      famillies: new UntypedFormArray((this.data?.defaults?.famillies || []).map(x => new UntypedFormControl(x))),
    });

    this.gouvSearchForm = new UntypedFormGroup({
      q: new UntypedFormControl(null),
      codePostal: new UntypedFormControl(null),
      ville: new UntypedFormControl(null),
      departement: new UntypedFormControl(null),
      activitePrincipale: new UntypedFormControl(null),
    });

    this.categoryService.getPurchaseFamilies().subscribe(families => {
      this.families = families;

      this.filteredFamilies = this.itemCtrl.valueChanges.pipe(
        startWith(null),
        map((item: string | null) => (item ? this.filterNormalized(this.families, item) : this.families.slice())),
      );
    });

    if (this.isUpdateMode()) {
      if (this.defaults?.tva !== null) {
        this.submittedToTva = true;
        this.form.controls.submittedToTva.setValue(true);
      }
    }

    this.addressForm.controls.country.valueChanges.subscribe(value => {
      this.isFrench = value ? FrenchTerritoryHelper(value) : true;
      if (!this.showGouvSearch) {
        this.resetGouvSearch();
      }
      if (!this.isIntern) {
        if (this.isFrench) {
          FormSwitchValidatorHelper(
            this.form,
            ['sirenNumber'],
            [Validators.required, Validators.pattern(/^[0-9]{9}$/)],
          );
          // ETRANGER : aucun contrôle sur le code postal, même si le pays est France.
          FormSwitchValidatorHelper(
            this.addressForm,
            ['postalCode'],
            this.isEtranger ? null : [Validators.pattern(/^[0-9]{5}$/)],
          );
          FormSwitchValidatorHelper(this.form, ['codeApe'], [Validators.required, Validators.pattern(/^\d{4}[A-Z]$/)]);
          FormSwitchValidatorHelper(this.form, ['siretNumber'], [Validators.required]);
          FormSwitchValidatorHelper(this.form, ['dunsNumber'], null);
        } else {
          FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe'], null);
          FormSwitchValidatorHelper(this.form, ['dunsNumber'], [Validators.required]);
          FormSwitchValidatorHelper(this.addressForm, ['postalCode']);

          // this.form.get('sirenNumber').setValue(null);
          // this.form.get('siretNumber').setValue(null);
          // this.form.get('codeApe').setValue(null);
        }
      }
    });

    this.form.controls.type.valueChanges.subscribe(value => {
      this.isIntern = value === SupplierType.INTERNE && !this.defaults?.supportEstablishment;
      this.isExterne = value === SupplierType.EXTERNE;
      this.isParticulier = value === SupplierType.PARTICULIER;
      this.isEtranger = value === SupplierType.ETRANGER;

      // La zone de recherche gouv n'est visible que pour un EXTERNE français : si le type change,
      // on referme l'état élargi (résultats + largeur de modale).
      if (!this.showGouvSearch) {
        this.resetGouvSearch();
      }

      // Le bloc adresse (Adresse, Code postal, Ville, Pays) est requis uniquement pour les types
      // qui l'affichent (EXTERNE / PARTICULIER / ETRANGER), pas pour INTERNE.
      this.switchAddressValidators(this.showSupplierDetails);

      // Le compte auxiliaire est requis (création et modification) pour les types qui l'affichent
      // (EXTERNE / PARTICULIER / ETRANGER), pas pour INTERNE.
      this.switchSubsidiarAccountValidators(this.showSupplierDetails);

      // INTERNE : ne rien valider
      if (this.isIntern) {
        this.form.controls.tva.setAsyncValidators(null);
        this.switchValidators(null);
        return;
      }

      // EXTERNE FR : SIRET / SIREN / Code APE / TVA validés
      if (this.isExterne && this.isFrench) {
        FormSwitchValidatorHelper(this.form, ['sirenNumber'], [Validators.required, Validators.pattern(/^[0-9]{9}$/)]);
        FormSwitchValidatorHelper(this.form, ['codeApe'], [Validators.required, Validators.pattern(/^\d{4}[A-Z]$/)]);
        FormSwitchValidatorHelper(this.form, ['siretNumber'], [Validators.required]);
        FormSwitchValidatorHelper(this.form, ['dunsNumber'], null);
        FormSwitchValidatorHelper(
          this.form,
          ['tva'],
          [
            Validators.pattern(
              /^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|GB|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK)[0-9A-Z]{9,11}$/,
            ),
          ],
        );
        return;
      }

      // EXTERNE non-FR : dunsNumber required
      if (this.isExterne && !this.isFrench) {
        FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe'], null);
        FormSwitchValidatorHelper(this.form, ['dunsNumber'], [Validators.required]);
        return;
      }

      // ETRANGER : Identifiant entreprise requis, pas de SIRET/SIREN/Code APE/TVA
      if (this.isEtranger) {
        FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe', 'tva'], null);
        FormSwitchValidatorHelper(this.form, ['dunsNumber'], [Validators.required]);
        this.nullFormIdentifierFields(false);
        return;
      }

      // PARTICULIER : aucun identifiant requis
      if (this.isParticulier) {
        FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe', 'tva', 'dunsNumber'], null);
        this.nullFormIdentifierFields(true);
        return;
      }
    });

    this.form.controls.submittedToTva.valueChanges.subscribe(value => {
      this.submittedToTva = value;
    });

    // En création, le type EXTERNE est pré-sélectionné par défaut. Le setValue déclenche la
    // subscription type.valueChanges ci-dessus (affichage des champs + validateurs adéquats).
    // L'utilisateur reste libre de changer de type.
    if (this.isCreateMode()) {
      this.form.controls.type.setValue(SupplierType.EXTERNE);
    }
  }

  // Lance une recherche (page 1) : la modale s'élargit et les résultats s'affichent à gauche de la fiche.
  searchGouv(): void {
    if (this.gouvHasNoCriteria || this.gouvLoading) {
      return;
    }
    this.gouvLoading = true;
    this.gouvPage = 1;
    const sub = this.service.searchGouvApi({ ...this.gouvSearchForm.value, page: 1 }).subscribe(
      result => {
        this.gouvResults = result.data;
        this.gouvTotalPage = result.totalPage;
        this.gouvLoading = false;
        this.hasSearchedGouv = true;
        this.selectedGouvSiret = null;
        this.dialogRef.updateSize(SupplierCreateUpdateDeleteComponent.GOUV_DIALOG_WIDE_WIDTH);
      },
      () => {
        this.gouvLoading = false;
        this.snackbarService.danger('La recherche d’entreprises a échoué, veuillez réessayer.');
      },
    );
    this.subscription.add(sub);
  }

  // Charge la page suivante et l'ajoute à la liste (l'API gouv est paginée).
  loadMoreGouv(): void {
    if (this.gouvLoading || !this.canLoadMoreGouv) {
      return;
    }
    this.gouvLoading = true;
    const sub = this.service.searchGouvApi({ ...this.gouvSearchForm.value, page: this.gouvPage + 1 }).subscribe(
      result => {
        const firstNewIndex = this.gouvResults.length;
        this.gouvPage = result.currentPage;
        this.gouvResults = [...this.gouvResults, ...result.data];
        this.gouvLoading = false;
        this.scrollGouvResultsToItem(firstNewIndex);
      },
      () => {
        this.gouvLoading = false;
        this.snackbarService.danger('La recherche d’entreprises a échoué, veuillez réessayer.');
      },
    );
    this.subscription.add(sub);
  }

  // Fait défiler la liste pour amener la première carte fraîchement chargée en haut de la zone visible.
  // setTimeout : attendre que le *ngFor ait rendu les nouvelles cartes avant de mesurer leur position.
  private scrollGouvResultsToItem(index: number): void {
    setTimeout(() => {
      const list = this.gouvResultsList?.nativeElement;
      // Les cartes sont les seuls enfants rendus de la liste quand il y a des résultats.
      const item = list?.children?.item(index) as HTMLElement | null;
      if (list && item) {
        const top = item.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
        list.scrollTo({ top, behavior: 'smooth' });
      }
    });
  }

  // Sélection d'une entreprise : préremplit la fiche fournisseur, qui reste modifiable avant ENREGISTRER.
  selectGouvCompany(company: SupplierGouvResultModel): void {
    this.selectedGouvSiret = company.siret;
    this.form.patchValue({
      label: company.label,
      commercialName: company.commercialName,
      sirenNumber: company.siren,
      siretNumber: company.siret,
      codeApe: company.codeApe,
      submittedToTva: !!company.tva,
      tva: company.tva,
    });
    this.addressForm.patchValue({
      street: company.address?.street,
      additionalAddress: company.address?.additionalAddress,
      postalCode: company.address?.postalCode,
      city: company.address?.city,
      country: company.address?.country ?? Country.FR,
    });
  }

  // Remet la fiche dans son état d'ouverture (création) : champs vides, type EXTERNE, pays FR.
  // La sélection est désélectionnée mais les résultats de recherche restent affichés,
  // pour permettre de repartir sur une autre entreprise.
  resetSupplierForm(): void {
    (this.form.get('famillies') as UntypedFormArray).clear();
    this.form.reset();
    this.addressForm.reset();
    // Rejoue les valeurs initiales de ngOnInit ; les setValue déclenchent les subscriptions
    // type/country (flags isExterne/isFrench + validateurs adéquats).
    this.addressForm.controls.country.setValue(Country.FR);
    this.form.controls.type.setValue(SupplierType.EXTERNE);
    this.form.controls.submittedToTva.setValue(false);
    this.form.controls.electronicInvoicePlatform.setValue(false);
    this.selectedGouvSiret = null;
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  // SIRET groupé 3-3-3-5 pour lisibilité (ex. 480 470 152 02984).
  formatSiret(siret: string): string {
    if (!siret) {
      return '';
    }
    return siret.replace(/^(\d{3})(\d{3})(\d{3})(\d{5})$/, '$1 $2 $3 $4');
  }

  // Referme l'état élargi (résultats + largeur) quand la recherche n'est plus applicable.
  private resetGouvSearch(): void {
    if (!this.hasSearchedGouv) {
      return;
    }
    this.hasSearchedGouv = false;
    this.gouvResults = [];
    this.gouvTotalPage = 0;
    this.gouvPage = 1;
    this.selectedGouvSiret = null;
    this.dialogRef.updateSize();
  }

  resetForm() {
    FormResetManyHelper(this.addressForm, ['street', 'postalCode', 'city', 'country', 'additionalAddress']);
    FormResetManyHelper(this.form, ['siretNumber', 'sirenNumber', 'dunsNumber', 'codeApe', 'tva', 'subsidiarAccount']);
  }

  switchValidators(validator: any) {
    FormSwitchValidatorHelper(
      this.form,
      ['siretNumber', 'sirenNumber', 'dunsNumber', 'codeApe', 'famillies'],
      validator,
    );
    FormSwitchValidatorHelper(this.addressForm, ['street', 'postalCode', 'city', 'country'], validator);
  }

  // Active (required) ou retire les validateurs du bloc adresse selon que le type l'affiche ou non.
  private switchAddressValidators(required: boolean): void {
    FormSwitchValidatorHelper(this.addressForm, ['street', 'city', 'country'], required ? [Validators.required] : null);
    // ETRANGER : aucun contrôle sur le code postal (formats étrangers non normés).
    FormSwitchValidatorHelper(
      this.addressForm,
      ['postalCode'],
      required && !this.isEtranger ? [Validators.required, Validators.pattern(/^[0-9]{5}$/)] : null,
    );
  }

  // Active (required) ou retire le validateur required du compte auxiliaire selon le type,
  // tout en conservant la contrainte de longueur max.
  private switchSubsidiarAccountValidators(required: boolean): void {
    FormSwitchValidatorHelper(
      this.form,
      ['subsidiarAccount'],
      required ? [Validators.required, Validators.maxLength(13)] : [Validators.maxLength(13)],
    );
  }

  beforeCreateItem() {
    if (!this.countries.includes(this.addressForm.controls.country.value)) {
      this.addressForm.controls.country.reset();
    }
    if (this.form.get('type').value === SupplierType.INTERNE) {
      this.defaults = {
        _id: this.defaults._id,
        type: this.defaults.type,
        label: this.defaults.label,
        sirenNumber: null,
        siretNumber: null,
        dunsNumber: null,
        tva: null,
        codeApe: null,
        headquarterAddress: null,
        famillies: this.form.get('famillies').value,
      };
    }

    if (this.form.get('type').value === SupplierType.PARTICULIER) {
      this.nullDefaultsIdentifierFields(true);
    }

    if (this.form.get('type').value === SupplierType.ETRANGER) {
      this.nullDefaultsIdentifierFields(false);
    }
    // cas ou c'est externe :
    this.defaults.famillies = this.form.get('famillies').value;

    if (this.addressForm.get('country').value !== Country.FR) {
      this.addressForm.controls.country.clearValidators();
    } else {
      this.addressForm.controls.country.clearValidators();
      this.addressForm.controls.country.addValidators([Validators.required]);
    }
  }

  afterCreateItem(result?: any, error?: any) {
    if (error) {
    } else {
      this.close(result);
    }
  }

  beforeUpdateItem() {
    if (!this.countries.includes(this.addressForm.controls.country.value)) {
      this.addressForm.controls.country.reset();
    }

    // isIntern est recalculé à false pour un interne lié (le disable() du champ Type émet un
    // valueChanges, cf. subscription de ngOnInit) : on inclut donc explicitement ce cas ici.
    if (this.isIntern || this.isLinkedInternSupplier) {
      this.defaults = {
        _id: this.defaults._id,
        type: this.defaults.type,
        label: this.defaults.label,
        sirenNumber: null,
        siretNumber: null,
        dunsNumber: null,
        tva: null,
        codeApe: null,
        headquarterAddress: null,
        famillies: this.form.get('famillies').value,
        subsidiarAccount: this.form.get('subsidiarAccount').value,
      };
      FormSwitchValidatorHelper(this.addressForm, ['city', 'country', 'postalCode', 'street'], null);
      FormSwitchValidatorHelper(
        this.form,
        ['siretNumber', 'sirenNumber', 'codeApe', 'dunsNumber', 'headquarterAddress', 'tva'],
        null,
      );
      // Le N° TVA hérité de l'établissement support (ex. "FR02 100 148 261") ne respecte pas le
      // pattern ni le contrôle TVA/siren : sans retrait de l'async, le formulaire reste invalide.
      this.form.controls.tva.clearAsyncValidators();
      this.form.controls.tva.updateValueAndValidity();
      this.switchSubsidiarAccountValidators(false);
    }

    if (this.isParticulier) {
      this.nullDefaultsIdentifierFields(true);
      FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe', 'dunsNumber', 'tva'], null);
    }

    if (this.isEtranger) {
      this.nullDefaultsIdentifierFields(false);
      FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe', 'tva'], null);
    }

    if (!this.isIntern && !this.isFrench) {
      this.defaults.sirenNumber = null;
      this.defaults.siretNumber = null;
      this.defaults.codeApe = null;
      FormSwitchValidatorHelper(this.form, ['siretNumber', 'sirenNumber', 'codeApe'], null);
    }

    if (!this.isIntern && this.isFrench) {
      this.defaults.dunsNumber = null;
      FormSwitchValidatorHelper(this.form, ['dunsNumber'], null);
    }

    if (this.form.controls.submittedToTva.value === false) {
      this.form.controls.tva.setValue('');
      this.defaults.tva = null;
    }

    if (this.defaults.sirenNumber === this.origin.sirenNumber) {
      this.form.controls.sirenNumber.removeValidators(Validators.required);
      this.form.getRawValue().sirenNumber = null;
    }

    if (this.defaults.siretNumber === this.origin.siretNumber) {
      this.form.controls.siretNumber.removeValidators(Validators.required);
      this.form.getRawValue().siretNumber = null;
    }
    this.defaults.famillies = this.form.get('famillies').value;
  }

  afterUpdateItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  afterImportItem(result?: any, error?: any) {
    if (result) {
      this.close(true);
    }
  }

  // ajout des méthodes pour le champs chips de famille d'achat ou poste de dépense
  // add(event: MatChipInputEvent): void {
  //     console.log(event)
  //     const value = (event.value || '').trim();
  //
  //     if (value) {
  //         this.familiesSupplier.push(value);
  //     }
  //
  //     event.chipInput!.clear();
  //
  //     this.itemCtrl.setValue(null);
  //     console.log(event, "-----event")
  // }

  remove(item: string): void {
    const index = this.form.get('famillies').value.indexOf(item);
    if (index >= 0) {
      this.form.get('famillies').value.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    // le boolean permet de générer un message d'erreur dans le chips si l'utilisateur a voulu mettre un doublon
    this.isDuplicate = false;
    const selectedFamily = event.option.value;
    const familiesArray = this.form.get('famillies').value;

    // Rechercher l'index de la famille en double dans le tableau
    const duplicateIndex = familiesArray.findIndex(family => family._id === selectedFamily._id);

    // Si l'élément est en double, le remplacer, sinon l'ajouter à la liste des chips
    if (duplicateIndex !== -1) {
      familiesArray[duplicateIndex] = selectedFamily;
      // Le message d'erreur se lancera en cas de tentative d'insertion de doublon.
      this.isDuplicate = true;
    } else {
      familiesArray.push(selectedFamily);
    }
    this.itemInput.nativeElement.value = '';
    this.itemCtrl.setValue(null);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }

  private nullFormIdentifierFields(includeDunsNumber: boolean): void {
    this.form.controls.siretNumber.setValue(null);
    this.form.controls.sirenNumber.setValue(null);
    this.form.controls.codeApe.setValue(null);
    this.form.controls.tva.setValue(null);
    this.form.controls.submittedToTva.setValue(false);
    if (includeDunsNumber) {
      this.form.controls.dunsNumber.setValue(null);
    }
  }

  private nullDefaultsIdentifierFields(includeDunsNumber: boolean): void {
    this.defaults.siretNumber = null;
    this.defaults.sirenNumber = null;
    this.defaults.codeApe = null;
    this.defaults.tva = null;
    if (includeDunsNumber) {
      this.defaults.dunsNumber = null;
    }
  }

  async createItem() {
    this.loading = true;
    this.defaults = this.form.getRawValue();
    this.beforeCreateItem();
    if (this.form.valid) {
      const sub = this.service.create(this.defaults).subscribe(
        result => {
          this.afterCreateItem(result, null);
          this.setSnackbar(ResponseTypeEnum.Success, CrudModeEnum.Create, result.message);
        },
        async error => {
          if (error.error.errors.map(x => x.statusCode === 5554)) {
            const foundSupplierIsArchive = error.error.errors.map(x => x.message).toString();
            this.supplierService.findById(foundSupplierIsArchive).subscribe(result => {
              result.data.isArchive = false;
              this.dialog
                .open(DialogConfirmComponent, {
                  data: {
                    title: `Ce SIRET existe déjà pour le fournisseur ${result.data.label}`,
                    description: `Merci de confirmer sa remise en service`,
                    cancelText: 'Annuler',
                    validText: 'Confirmer',
                    call$: this.supplierService.update(result.data),
                  },
                })
                .afterClosed()
                .subscribe(finalResult => {
                  if (finalResult) {
                    this.dialogRef.close();
                    this.snackbarService.success('Le fournisseur a été remis en service avec succès');
                  }
                });
            });
          }

          this.afterCreateItem(null, error);
          this.setErrorsMessages(error.error.errors);
          this.loading = false;
        },
      );
      this.subscription.add(sub);
    } else {
      this.onFormInvalid();
      this.logFormErrors(this.form);
      this.form.markAllAsTouched();
      this.loading = false;
    }
  }
}
